import React, { useState } from 'react';
import {
  Typography,
  Chip,
  IconButton,
  Divider,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  Box,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';

// IMPORT SERVICES
import { downloadFileForPreview } from '../../../services/userFileService';

// IMPORT PREVIEWERS
import SimplePdfPreviewDialog from '../../PIIAnonymizerPage/PIIFileUploadPanel/SimplePdfPreviewDialog';
import PreviewWord from '../../PIIAnonymizerPage/PIIFileUploadPanel/PreviewWord';
import PreviewJSON from '../../PIIAnonymizerPage/PIIFileUploadPanel/PreviewJSON';

const PIIRelatedFilesDialog = ({ open, onClose, relatedFiles, t, onError }) => {
  const [filePreviewLoadingId, setFilePreviewLoadingId] = useState(null);
  const [activeFilePreview, setActiveFilePreview] = useState(null);

  const handleOpenFilePreview = async (file) => {
    const fileNameLower = (file.filename || '').toLowerCase();
    const isPdf = fileNameLower.endsWith('.pdf');
    const isDocx = fileNameLower.endsWith('.docx');
    const isJson = fileNameLower.endsWith('.json');

    if (!isPdf && !isDocx && !isJson) {
      onError(t('files.unsupported_preview', 'Tento typ súboru nepodporuje náhľad.'), 'warning');
      return;
    }

    try {
      setFilePreviewLoadingId(file.id);

      const res = await downloadFileForPreview(file);
      if (!res?.success || !res.data?.blob) {
        throw new Error(res?.error || "Neznáma chyba pri sťahovaní");
      }

      let type = 'pdf';
      if (isDocx) type = 'docx';
      if (isJson) type = 'json';

      setActiveFilePreview({
        type,
        fileItem: {
          blob: res.data.blob,
          name: file.filename,
          downloadName: file.filename,
        }
      });
    } catch (err) {
      console.error('Failed to load file preview', err);
      onError(t('files.preview_failed', 'Nepodarilo sa načítať súbor na náhľad.'));
    } finally {
      setFilePreviewLoadingId(null);
    }
  };

  const handleCloseFilePreview = () => {
    setActiveFilePreview(null);
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            bgcolor: 'background.paper',
            py: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('admin.pii.protocol.relatedFilesTitle', 'Súvisiace súbory z procesu')}
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ p: 0 }}>
          <List sx={{ py: 1 }}>
            {relatedFiles.map((file, idx) => {
              const isPdf = (file.filename || '').toLowerCase().endsWith('.pdf');
              const isDocx = (file.filename || '').toLowerCase().endsWith('.docx');
              const isJson = (file.filename || '').toLowerCase().endsWith('.json');
              const canPreview = isPdf || isDocx || isJson;

              return (
                <React.Fragment key={file.id}>
                  <ListItem sx={{ py: 1.5, px: 3, '&:hover': { bgcolor: 'action.hover' } }}>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600}>
                          {file.filename}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                          {file.origin && (
                            <Chip 
                              label={file.origin === 'external_anonymizer' ? t('files.origin_external', 'Cenzor AI') : file.origin} 
                              size="small" 
                              color={file.origin === 'external_anonymizer' ? 'info' : 'default'}
                              sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1.5 }} 
                            />
                          )}
                          {file.process_type && (
                            <Chip 
                              label={file.process_type} 
                              size="small" 
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.65rem', borderRadius: 1.5 }} 
                            />
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {canPreview && (
                        <Tooltip title={t('files.preview', 'Náhľad')}>
                          <span>
                            <IconButton
                              onClick={() => handleOpenFilePreview(file)}
                              disabled={filePreviewLoadingId === file.id}
                              color="primary"
                              size="small"
                              sx={{ bgcolor: 'action.hover' }}
                            >
                              {filePreviewLoadingId === file.id ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                  {idx < relatedFiles.length - 1 && <Divider component="li" sx={{ mx: 3 }} />}
                </React.Fragment>
              );
            })}
          </List>
        </DialogContent>

        <DialogActions sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Button onClick={onClose} sx={{ fontWeight: 600 }}>
            {t('common.close', 'Zavrieť')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 3. FILE PREVIEW MODALS */}
      {activeFilePreview?.type === 'pdf' && (
        <SimplePdfPreviewDialog
          open={true}
          title={activeFilePreview.fileItem.name}
          fileBlob={activeFilePreview.fileItem.blob}
          onClose={handleCloseFilePreview}
          t={t}
        />
      )}

      {activeFilePreview?.type === 'docx' && (
        <PreviewWord
          open={true}
          fileItem={activeFilePreview.fileItem}
          onClose={handleCloseFilePreview}
        />
      )}

      {activeFilePreview?.type === 'json' && (
        <PreviewJSON
          open={true}
          fileItem={activeFilePreview.fileItem}
          onClose={handleCloseFilePreview}
        />
      )}
    </>
  );
};

export default PIIRelatedFilesDialog;